import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import { ModalForm, useModalDispatch } from "collections/Modal";
import { array, boolean, InferType, mixed, number, object, string } from "yup";
import { useTranslation } from "utilities/i18n";
import {
  useAssignEntitlementValueMutation,
  useInstanceListQuery,
  useReleaseEntitlementValueMutation
} from "generated/graphql";
import {
  DISMISS,
  Field,
  FieldProps,
  Form,
  Input,
  useNotification
} from "collections";
import {
  getInstanceIdentifiableName,
  getProductName,
  toLicenseUnlimitedCountString
} from "utilities/licenseServerUtils";
import { EntitlementGrouping } from "../useEntitlements";
import {
  sortTableData,
  TableBody,
  TableBulkItemRow,
  TableHeaderItem
} from "collections/Table";
import { SortState } from "store/filters";
import "./AssignEntitlementGroupingsModal.scss";
import {
  CancelButton,
  DetailsLoader,
  NoResultsIndicator,
  SubmitButton
} from "elements";
import { handleNullFields } from "utilities/stringUtils";
import { FieldArray } from "formik";
import { Card, makeColumnContentForCard } from "collections/Cards";

export type InstanceInfo = {
  sourceIndex: number;
  recordId: string;
  name: string | undefined;
  productId: string;
  productName: string | undefined;
  productVersion: string;
  // TODO: Consider splitting into two different types to avoid multiple type confusion
  initialAssignedAttributes: number | boolean;
  assignedAttributes: number | boolean;
};

type AssignmentExecutionContext = {
  instance: InstanceInfo;
  newValue: number;
  variables: {
    groupingId: string;
    instanceRecordId: string;
    value: number;
  };
};

type AssignmentStatus = {
  assignedCount: number;
  availableCount: number;
};

export const AssignEntitlementGroupingsSchema = object({
  entitlement: object<
    Pick<
      EntitlementGrouping,
      "groupingId" | "isUnlimitedBoolean" | "availableCount"
    >
  >({
    groupingId: string(),
    isUnlimitedBoolean: boolean(),
    availableCount: mixed()
  }),
  instances: array(
    object<InstanceInfo>({
      sourceIndex: number(),
      recordId: string(),
      name: string(),
      productId: string(),
      productName: string(),
      productVersion: string(),
      initialAssignedAttributes: mixed(),
      assignedAttributes: mixed()
    })
  )
});

export type AssignEntitlementGroupingsSchemaType = InferType<
  typeof AssignEntitlementGroupingsSchema
>;

const useInstancesQuery = (
  tenantId: string | undefined,
  entitlement: EntitlementGrouping | undefined
) => {
  const queryResults = useInstanceListQuery({
    variables: {
      tenantId: tenantId ?? ""
    },
    requestPolicy: "network-only",
    pause: tenantId === undefined
  });

  const loading = queryResults.fetching || queryResults.stale;
  const validateData = !loading;
  const dataView = validateData ? queryResults.data?.searchResult : undefined;

  const data: Array<InstanceInfo> = useMemo(() => {
    const result =
      dataView?.edges.map((entry, index) => {
        const node = entry.node;

        const isUnlimitedBoolean = entitlement?.isUnlimitedBoolean;

        const entitlementGroupingAssignmentSet = entitlement?.assignmentMap.get(
          node.recordId
        );
        const totalAssignedValueSum =
          entitlementGroupingAssignmentSet?.totalAssignedValueSum ?? 0;
        const assignedAttributes = isUnlimitedBoolean
          ? totalAssignedValueSum !== 0
          : totalAssignedValueSum;
        return {
          sourceIndex: index,
          recordId: node.recordId,
          name: getInstanceIdentifiableName(node),
          productId: node.productId,
          productName: getProductName(node.productId),
          productVersion: node.productVersion,
          assignedAttributes: assignedAttributes,
          initialAssignedAttributes: assignedAttributes
        };
      }) ?? [];
    return result;
  }, [dataView, entitlement]);

  return { ...queryResults, data };
};

const useEntitlementInstancesQuery = (
  tenantId: string | undefined,
  entitlement: EntitlementGrouping | undefined
) => {
  const queryResults = useInstancesQuery(tenantId, entitlement);
  const productId = entitlement?.productId;

  const validateData = !(queryResults.fetching || queryResults.stale);
  const dataView = validateData ? queryResults.data : undefined;

  const instances: Array<InstanceInfo> = useMemo(() => {
    let result = dataView ?? [];
    if (result && productId) {
      const instanceCount = result.length;
      result = result.filter(
        instance => instance.productId.localeCompare(productId) === 0
      );
      if (result.length < instanceCount) {
        // Fix sourceIndex values since instances were filtered out
        result.forEach((instance, index) => {
          instance.sourceIndex = index;
        });
      }
    }
    return result;
  }, [dataView, productId]);

  const data: AssignEntitlementGroupingsSchemaType = {
    entitlement: {
      groupingId: entitlement?.groupingId ?? "",
      isUnlimitedBoolean: entitlement?.isUnlimitedBoolean ?? false,
      availableCount: entitlement?.availableCount ?? 0
    },
    instances: instances
  };

  return { ...queryResults, data };
};

export const EntitlementGroupingInformation: React.FC<{
  entitlement: EntitlementGrouping | undefined;
  totalAvailableCount: AssignmentStatus;
}> = ({ entitlement, totalAvailableCount }) => {
  const { t } = useTranslation();

  const fields = [
    {
      key: "name",
      label: t("ENTITLEMENT"),
      content: <p>{handleNullFields(entitlement?.name)}</p>
    },
    {
      key: "type",
      label: t("TYPE"),
      content: <p>{handleNullFields(entitlement?.productName)}</p>
    },
    {
      key: "version",
      label: t("VERSION_ALT"),
      content: <p>{handleNullFields(entitlement?.productVersion)}</p>
    },
    {
      key: "attributeType",
      label: t("ATTRIBUTE_TYPE"),
      content: <p>{handleNullFields(entitlement?.attributeTypeName)}</p>
    },
    {
      key: "attributeCount",
      label: t("ATTRIBUTE_COUNT"),
      content: (
        <p>{toLicenseUnlimitedCountString(entitlement?.attributeCount)}</p>
      )
    },
    {
      key: "assignedCount",
      label: t("ASSIGNED_ATTRIBUTES"),
      content: (
        <p>
          {toLicenseUnlimitedCountString(totalAvailableCount.assignedCount)}
        </p>
      )
    },
    {
      key: "removedCount",
      label: t("REMOVED_ATTRIBUTES"),
      content: <p>{toLicenseUnlimitedCountString(entitlement?.removedCount)}</p>
    },
    {
      key: "availableCount",
      label: t("AVAILABLE_ATTRIBUTES"),
      content: (
        <p>
          {toLicenseUnlimitedCountString(totalAvailableCount.availableCount)}
        </p>
      )
    }
  ];

  return (
    <>
      <Card className="table-card">{makeColumnContentForCard(fields, 4)}</Card>
    </>
  );
};

type InstanceRowProps = {
  instance: InstanceInfo;
  entitlement: EntitlementGrouping | undefined;
  index: number;
};

export const InstanceRow: React.FC<InstanceRowProps> = ({
  instance,
  entitlement,
  index
}) => {
  const assignedAttributesInputName = `instances[${instance.sourceIndex}].assignedAttributes`;
  return (
    <TableBulkItemRow showChecks={false} onCheck={() => {}} checked={false}>
      <td>{instance.name}</td>
      <td>{instance.initialAssignedAttributes}</td>
      <td>
        <div className="form-input">
          {entitlement?.isUnlimitedBoolean ? (
            <Input
              type="checkbox"
              name={assignedAttributesInputName}
              tabIndex={index}
            />
          ) : (
            <Input
              type="number"
              name={assignedAttributesInputName}
              tabIndex={index}
            />
          )}
        </div>
      </td>
    </TableBulkItemRow>
  );
};

const Instances: React.FC<{
  entitlement: EntitlementGrouping | undefined;
  values: {
    instances: Array<InstanceInfo>;
  };
  loading: boolean;
  setFirstTableIndex: Dispatch<SetStateAction<number>>;
  setTotalAvailableCount: Dispatch<SetStateAction<AssignmentStatus>>;
}> = ({
  entitlement,
  values,
  loading,
  setFirstTableIndex,
  setTotalAvailableCount
}) => {
  const { t } = useTranslation();
  const instances = values.instances;

  useEffect(() => {
    if (entitlement) {
      if (entitlement.isUnlimitedBoolean) {
        setTotalAvailableCount({ assignedCount: -1, availableCount: -1 });
      } else {
        const sum = instances.reduce((total, instance) => {
          return total + +instance.assignedAttributes;
        }, 0);
        const availableCount = Math.max(
          entitlement.attributeCount - sum - entitlement.removedCount,
          0
        );
        const assignedCount =
          entitlement.attributeCount -
          availableCount -
          entitlement.removedCount;
        setTotalAvailableCount({ assignedCount, availableCount });
      }
    }
  }, [entitlement, instances, setTotalAvailableCount]);

  const [currentSort, setSort] = useState<SortState>({
    field: "name",
    direction: "DESC"
  });

  const tableData = useMemo(() => {
    let result: Array<InstanceInfo>;
    if (instances.length > 0) {
      result = [...instances];
      sortTableData(result, currentSort);
    } else {
      result = [];
    }
    return result;
  }, [instances, currentSort]);

  const sourceIndex = tableData.length > 0 ? tableData[0].sourceIndex : 0;
  useEffect(() => {
    setFirstTableIndex(() => {
      return sourceIndex;
    });
  }, [sourceIndex, setFirstTableIndex]);

  const columns = [
    { key: "name", label: t("NAME") },
    {
      key: "initialAssignedAttributes",
      label: t("CURRENT_ATTRIBUTE_COUNT")
    },
    {
      key: "assignedAttributes",
      label: t("UPDATE_ATTRIBUTE_COUNT")
    }
  ];

  return (
    <FieldArray
      name="instances"
      render={() => (
        <>
          <div className="mt-1 mb-1">
            <b>
              {t("PURCHASED_PRODUCT", {
                count: tableData.length
              })}
            </b>
          </div>
          <div className="table-container">
            <table className="table custom-table">
              <thead>
                <tr>
                  {columns.map(({ key, label }) => (
                    <TableHeaderItem
                      fieldName={key}
                      title={label}
                      sort={{ currentSort, onSort: setSort }}
                      key={key}
                    />
                  ))}
                </tr>
              </thead>
              <TableBody
                loading={loading}
                cols={columns.length - 1}
                accommodateScrollbar
              >
                {tableData.map((instance, index) => {
                  return (
                    <InstanceRow
                      key={instance.recordId}
                      instance={instance}
                      entitlement={entitlement}
                      index={index}
                    />
                  );
                })}
                {!loading && !tableData.length && (
                  <tr>
                    <td>
                      <NoResultsIndicator
                        className="inline mt-5"
                        title={t("NO_PURCHASED_PRODUCTS_FOUND")}
                      />
                    </td>
                  </tr>
                )}
              </TableBody>
            </table>
          </div>
        </>
      )}
    />
  );
};

export const AssignEntitlementGroupingsModal: React.FC<{
  refresh: () => void;
  tenantId: string | undefined;
  entitlements: Array<EntitlementGrouping>;
}> = ({ entitlements, tenantId, refresh }) => {
  const { t } = useTranslation();
  const [, assignEntitlementValue] = useAssignEntitlementValueMutation();
  const [, releaseEntitlementValue] = useReleaseEntitlementValueMutation();
  const closeModal = useModalDispatch();
  const [addNotification] = useNotification();
  // TODO: This is here because there is potential for future multiple selection support to
  // be defined by UX
  const entitlement = entitlements.length > 0 ? entitlements[0] : undefined;

  const queryResults = useEntitlementInstancesQuery(tenantId, entitlement);

  const [firstTableIndex, setFirstTableIndex] = useState(0);

  const [totalAvailableCount, setTotalAvailableCount] = useState<
    AssignmentStatus
  >({
    assignedCount: entitlement?.assignedCount ?? 0,
    availableCount: entitlement?.availableCount ?? 0
  });

  const dynamicAssignEntitlementGroupingsSchema = AssignEntitlementGroupingsSchema;

  const initialValues: AssignEntitlementGroupingsSchemaType = {
    entitlement: {
      groupingId: entitlement?.groupingId ?? "",
      isUnlimitedBoolean: entitlement?.isUnlimitedBoolean ?? false,
      availableCount: entitlement?.availableCount ?? 0
    },
    instances: queryResults.data?.instances ?? []
  };

  const autoDistribute = (
    values: AssignEntitlementGroupingsSchemaType,
    setValues: (values: AssignEntitlementGroupingsSchemaType) => void
  ) => {
    if (entitlement) {
      const instances = values.instances;
      if (instances.length > 0) {
        let newInstances;
        if (entitlement.isUnlimitedBoolean) {
          newInstances = values.instances.map(instance => {
            return {
              ...instance,
              assignedAttributes: true
            };
          });
        } else {
          const availableAttributeCount = Math.max(
            entitlement.attributeCount - entitlement.removedCount,
            0
          );
          const newAttributeValue = Math.max(
            Math.floor(availableAttributeCount / instances.length),
            0
          );
          const remainderValue =
            availableAttributeCount - newAttributeValue * instances.length;

          newInstances = values.instances.map(instance => {
            return {
              ...instance,
              assignedAttributes: newAttributeValue
            };
          });

          newInstances[firstTableIndex].assignedAttributes += remainderValue;
        }
        setValues({
          entitlement: {
            ...values.entitlement
          },
          instances: newInstances
        });
      }
    }
  };

  const onSubmit = async (values: AssignEntitlementGroupingsSchemaType) => {
    let success = true;

    const releaseValueList: Array<AssignmentExecutionContext> = [];
    const assignValueList: Array<AssignmentExecutionContext> = [];

    values.instances
      // Filter out instances with no assignment changes
      .filter(
        instance =>
          instance.assignedAttributes !== instance.initialAssignedAttributes
      )
      // Build execution context
      .map(instance => {
        let newValue;
        if (entitlement?.isUnlimitedBoolean) {
          newValue = instance.assignedAttributes ? 1 : -1;
        } else {
          newValue =
            +instance.assignedAttributes - +instance.initialAssignedAttributes;
        }
        const assignmentContext = {
          instance: instance,
          newValue: newValue,
          variables: {
            groupingId: values.entitlement.groupingId,
            instanceRecordId: instance.recordId,
            value: Math.abs(newValue)
          }
        };
        return assignmentContext;
      })
      // Group into assign and release change lists
      .forEach(assignmentContext => {
        if (assignmentContext.newValue > 0) {
          assignValueList.push(assignmentContext);
        } else {
          releaseValueList.push(assignmentContext);
        }
      });

    // Release all entitlement values from highest to lowest values (shouldn't matter, just to be consistent).
    const resp = await Promise.all(
      releaseValueList
        .sort((a, b) => b.newValue - a.newValue)
        .map(async assignmentContext => {
          const { error } = await releaseEntitlementValue(
            assignmentContext.variables
          );
          return {
            entitlement: entitlement,
            instanceName: assignmentContext.instance.name,
            error: error
          };
        })
    );

    // Assign all entitlements from least to greatest values (shouldn't matter, just to be consistent).
    resp.push(
      ...(await Promise.all(
        assignValueList
          .sort((a, b) => b.newValue - a.newValue)
          .map(async assignmentContext => {
            const { error } = await assignEntitlementValue(
              assignmentContext.variables
            );
            return {
              entitlement: entitlement,
              instanceName: assignmentContext.instance.name,
              error: error
            };
          })
      ))
    );

    // Process result for errors
    if (resp) {
      if (resp.length > 0) {
        resp.forEach(entitlementResp => {
          if (entitlementResp.error) {
            success = false;
            addNotification({
              type: "error",
              dismiss: DISMISS.DEFAULT,
              message: t("ERROR_ASSIGNING_ENTITLEMENT", {
                name: handleNullFields(entitlementResp.entitlement?.name),
                instanceName: entitlementResp.instanceName,
                message: entitlementResp.error
              })
            });
          }
        });

        if (success) {
          closeModal();
          refresh();

          addNotification({
            type: "success",
            dismiss: DISMISS.DEFAULT,
            message: t("SUCCESSFULLY_ASSIGNED_ENTITLEMENTS"),
            persistThroughNav: true
          });
        }
      } else {
        closeModal();
      }
    }
  };

  return (
    <ModalForm
      title={t("ASSIGN_ENTITLEMENTS")}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"AssignEntitlementGroupingsModal"}
      validationSchema={dynamicAssignEntitlementGroupingsSchema}
      className="fluid-height-ignore fluid-xl-ignore full assign-entitlements"
      closeIcon={true}
      fluid={false}
      enableReinitialize={true}
    >
      {({ setValues, values, isSubmitting }) => (
        <Form>
          <div className="form-content">
            <DetailsLoader
              loading={isSubmitting}
              title={t("ASSIGNING_ENTITLEMENTS")}
            >
              <EntitlementGroupingInformation
                entitlement={entitlement}
                totalAvailableCount={totalAvailableCount}
              />
              <Instances
                entitlement={entitlement}
                values={values}
                loading={queryResults.fetching || queryResults.stale}
                setFirstTableIndex={setFirstTableIndex}
                setTotalAvailableCount={setTotalAvailableCount}
              />
            </DetailsLoader>
          </div>
          <div className="btn-container">
            <CancelButton onClick={closeModal} />
            <button
              className="btn"
              type="button"
              disabled={isSubmitting}
              onClick={() => autoDistribute(values, setValues)}
            >
              {t("ASSIGN_DISTRIBUTE_ASSETS_AUTO_DISTRIBUTE")}
            </button>
            <Field name="reset">
              {({ form: { dirty, handleReset } }: FieldProps) => {
                return (
                  <button
                    type="button"
                    className="btn"
                    disabled={!dirty}
                    onClick={handleReset}
                  >
                    {t("DISCARD")}
                  </button>
                );
              }}
            </Field>
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  testId="save"
                  disabled={isSubmitting || !isValid || !dirty}
                  isSubmitting={isSubmitting}
                  labelKey="ASSIGN_ENTITLEMENTS"
                />
              )}
            </Field>
          </div>
        </Form>
      )}
    </ModalForm>
  );
};
